import { faCheck, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Description, Field, Label, Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import classnames from 'classnames';
import React, { Fragment, forwardRef } from 'react';

import { useThemeClasses } from '@/lib/legacy-theme/theme-utils';

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface SelectProps {
  label?: string;
  helperText?: string;
  error?: string;
  success?: string;
  variant?: 'default' | 'error' | 'success';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  placeholder?: string;
  options: SelectOption[];
  value?: string | number;
  onChange?: (value: string | number) => void;
  disabled?: boolean;
  required?: boolean;
}

export const Select = forwardRef<HTMLButtonElement, SelectProps>(
  ({ label, helperText, error, success, variant = 'default', size = 'md', fullWidth = false, placeholder = 'Select an option', options, value, onChange, disabled, required, ...props }, ref) => {
    // Base button classes using theme-aware variables
    const baseButtonClasses =
      'relative w-full flex flex-row items-center justify-between rounded-theme-md border font-theme-normal transition-theme-normal focus:outline-none focus:ring-2 focus:ring-offset-2 cursor-pointer';

    // Theme-aware variant classes for button
    const variantButtonClasses = {
      default: 'border-secondary-700 bg-secondary text-text hover:bg-secondary-hover focus:border-primary focus:ring-primary',
      error: 'border-error bg-secondary text-text hover:bg-secondary-hover focus:border-error focus:ring-error',
      success: 'border-success bg-secondary text-text hover:bg-secondary-hover focus:border-success focus:ring-success',
    };

    // Theme-aware size classes for button
    const sizeButtonClasses = {
      sm: 'px-sm py-xs text-sm min-h-[2rem]',
      md: 'px-md py-sm text-base min-h-[2.5rem]',
      lg: 'px-lg py-md text-lg min-h-[3rem]',
    };

    // Options container classes
    const optionsClasses = useThemeClasses('absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-theme-md border border-border bg-secondary py-1 shadow-lg focus:outline-none');

    // Option classes
    const optionClasses = useThemeClasses('relative cursor-pointer select-none py-2 pl-10 pr-4 text-text hover:bg-secondary-500 focus:bg-secondary-500 focus:outline-none');

    // Selected option classes
    const selectedOptionClasses = useThemeClasses('bg-primary-hover text-primary');

    // Disabled option classes
    const disabledOptionClasses = useThemeClasses('opacity-50 cursor-not-allowed');

    // Icon classes
    const iconClasses = useThemeClasses('flex-shrink-0 text-text-muted transition-transform duration-200');

    // Check icon classes
    const checkIconClasses = useThemeClasses('absolute left-2 top-1/2 transform -translate-y-1/2 text-primary');

    // Determine the actual variant based on error/success props
    const actualVariant = error ? 'error' : success ? 'success' : variant;

    // Combine button classes
    const buttonClasses = useThemeClasses(
      classnames(baseButtonClasses, variantButtonClasses[actualVariant], sizeButtonClasses[size], fullWidth && 'w-full', disabled && 'opacity-50 cursor-not-allowed'),
    );

    // Container classes
    const containerClasses = useThemeClasses(classnames('relative  flex flex-col gap-2', fullWidth && 'w-full'));

    // Label classes
    const labelClasses = useThemeClasses(classnames('block font-semibold text-sm text-text mb-xs', disabled && 'opacity-50'));

    // Helper text classes
    const helperTextClasses = useThemeClasses(classnames('mt-xs text-xs', error ? 'text-error' : success ? 'text-success' : 'text-text-muted'));

    // Selected option display text
    const selectedOption = options.find((option) => option.value === value);

    return (
      <Field className={containerClasses}>
        {label && (
          <Label className={labelClasses}>
            {label}
            {required && <span className="text-error ml-1">*</span>}
          </Label>
        )}

        <Listbox value={value} onChange={onChange} disabled={disabled}>
          <div className="relative">
            <ListboxButton ref={ref} className={buttonClasses} {...props}>
              <span className={classnames('block truncate px-3', !selectedOption && 'text-muted')}>{selectedOption ? selectedOption.label : placeholder}</span>
              <span className="px-3">
                <FontAwesomeIcon icon={faChevronDown} className={classnames(iconClasses, 'ui-open:rotate-180')} />
              </span>
            </ListboxButton>

            <ListboxOptions className={optionsClasses}>
              {options.map((option) => (
                <ListboxOption key={option.value} value={option.value} disabled={option.disabled} as={Fragment}>
                  {({ active, selected, disabled: optionDisabled }) => (
                    <li className={classnames(optionClasses, active && !optionDisabled && 'bg-secondary-800', selected && selectedOptionClasses, optionDisabled && disabledOptionClasses)}>
                      {selected && <FontAwesomeIcon icon={faCheck} className={checkIconClasses} />}
                      <span className={classnames('block truncate', selected && 'font-medium')}>{option.label}</span>
                    </li>
                  )}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        </Listbox>

        {(helperText || error || success) && <Description className={helperTextClasses}>{error || success || helperText}</Description>}
      </Field>
    );
  },
);

Select.displayName = 'Select';

export default Select;
